import { POSITION_SLOT_STATUS } from 'domain/people/positionSlots/constants';
import type { PositionSlotListItem } from 'domain/people/positionSlots/types';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import {
  WORKFORCE_ANALYTICS_LEVEL_COLUMNS,
  WORKFORCE_ANALYTICS_SUMMARY_ROW_LABELS,
  WORKFORCE_ANALYTICS_TOTAL_COLUMN,
  WorkforceAnalyticsViewBy,
  type WorkforceAnalyticsColumn,
} from 'domain/people/workforceAnalytics/constants';
import type { WorkforceAnalyticsCell, WorkforceAnalyticsRow } from 'domain/people/workforceAnalytics/types';

export const calculateWorkforceAnalyticsFillRate = (filled: number, total: number) => {
  if (!total) return 0;

  return Math.round((filled / total) * 100);
};

export const formatWorkforceAnalyticsCell = (cell: WorkforceAnalyticsCell) => {
  if (!cell.total) return EMPTY_VALUE_INDICATOR;

  return `${cell.filled} / ${cell.total} (${cell.fillRate}%)`;
};

const buildWorkforceAnalyticsCell = (
  column: WorkforceAnalyticsColumn,
  positionSlots: PositionSlotListItem[],
): WorkforceAnalyticsCell => {
  const filledPositionSlots = positionSlots.filter(positionSlot => positionSlot.status === POSITION_SLOT_STATUS.FILLED);
  const filled = filledPositionSlots.length;
  const total = positionSlots.length;

  return {
    column,
    filled,
    total,
    fillRate: calculateWorkforceAnalyticsFillRate(filled, total),
    positionSlotIds: positionSlots.map(positionSlot => positionSlot.id),
  };
};

const buildWorkforceAnalyticsCells = (
  positionSlots: PositionSlotListItem[],
): Record<WorkforceAnalyticsColumn, WorkforceAnalyticsCell> => {
  const positionSlotsByLevel = new Map<string, PositionSlotListItem[]>();

  positionSlots.forEach(positionSlot => {
    const { competencyLevel } = positionSlot;
    if (!competencyLevel) return;

    const levelPositionSlots = positionSlotsByLevel.get(competencyLevel) || [];

    levelPositionSlots.push(positionSlot);
    positionSlotsByLevel.set(competencyLevel, levelPositionSlots);
  });

  const cells = {
    [WORKFORCE_ANALYTICS_TOTAL_COLUMN]: buildWorkforceAnalyticsCell(WORKFORCE_ANALYTICS_TOTAL_COLUMN, positionSlots),
  } as Record<WorkforceAnalyticsColumn, WorkforceAnalyticsCell>;

  WORKFORCE_ANALYTICS_LEVEL_COLUMNS.forEach(column => {
    cells[column] = buildWorkforceAnalyticsCell(column, positionSlotsByLevel.get(column) || []);
  });

  return cells;
};

const getWorkforceAnalyticsGroupLabel = (positionSlot: PositionSlotListItem, viewBy: WorkforceAnalyticsViewBy) => {
  if (viewBy === WorkforceAnalyticsViewBy.BUSINESS_FUNCTION) return positionSlot.businessFunctionLabel;

  return positionSlot.positionDisplayName;
};

const buildWorkforceAnalyticsRow = (
  id: string,
  label: string,
  isSummary: boolean,
  positionSlots: PositionSlotListItem[],
): WorkforceAnalyticsRow => ({
  id,
  label,
  isSummary,
  cells: buildWorkforceAnalyticsCells(positionSlots),
});

const groupWorkforceAnalyticsPositionSlots = (
  positionSlots: PositionSlotListItem[],
  viewBy: WorkforceAnalyticsViewBy,
) => {
  const positionSlotsByLabel = new Map<string, PositionSlotListItem[]>();

  positionSlots.forEach(positionSlot => {
    const label = getWorkforceAnalyticsGroupLabel(positionSlot, viewBy);
    const groupPositionSlots = positionSlotsByLabel.get(label) || [];

    groupPositionSlots.push(positionSlot);
    positionSlotsByLabel.set(label, groupPositionSlots);
  });

  return Array.from(positionSlotsByLabel.entries()).sort(([firstLabel], [secondLabel]) =>
    firstLabel.localeCompare(secondLabel),
  );
};

export const buildWorkforceAnalyticsRows = (
  positionSlots: PositionSlotListItem[],
  viewBy: WorkforceAnalyticsViewBy,
): WorkforceAnalyticsRow[] => {
  const summaryRow = buildWorkforceAnalyticsRow(
    'summary',
    WORKFORCE_ANALYTICS_SUMMARY_ROW_LABELS[viewBy],
    true,
    positionSlots,
  );
  const rows = groupWorkforceAnalyticsPositionSlots(positionSlots, viewBy).map(([label, groupPositionSlots]) =>
    buildWorkforceAnalyticsRow(label, label, false, groupPositionSlots),
  );

  return [summaryRow, ...rows];
};
